import React, { useState, useEffect, useCallback } from 'react';
import {
  getTaxConfig, updateTaxConfig, getTaxCalendar, generateTaxForm, getTaxForms,
  getTaxForm, submitTaxForm, getLibroEmitidas, getLibroRecibidas, getAgingReport
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { PageTransition, StaggerContainer, StaggerItem } from '../components/motion/Animations';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  FileText, Calculator, BookOpen, Calendar, Settings, CheckCircle2, Clock,
  AlertTriangle, Download, ChevronDown, ChevronRight, Loader2, RefreshCw, Send
} from 'lucide-react';

const fmt = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);
const YEAR = new Date().getFullYear();

const TaxPage = () => {
  const [tab, setTab] = useState('models');
  const [config, setConfig] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [forms, setForms] = useState([]);
  const [libro, setLibro] = useState(null);
  const [libroType, setLibroType] = useState('emitidas');
  const [libroYear, setLibroYear] = useState(YEAR);
  const [libroQ, setLibroQ] = useState(null);
  const [aging, setAging] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cfgRes, calRes, formsRes] = await Promise.all([
        getTaxConfig(), getTaxCalendar(), getTaxForms({ year: YEAR })
      ]);
      setConfig(cfgRes.data);
      setCalendar(calRes.data);
      setForms(formsRes.data);
    } catch { toast.error('Error cargando datos fiscales'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchLibro = useCallback(async () => {
    try {
      const fn = libroType === 'emitidas' ? getLibroEmitidas : getLibroRecibidas;
      const { data } = await fn({ year: libroYear, quarter: libroQ || undefined });
      setLibro(data);
    } catch { toast.error('Error cargando libro'); }
  }, [libroType, libroYear, libroQ]);

  useEffect(() => { if (tab === 'books') fetchLibro(); }, [tab, fetchLibro]);

  const fetchAging = useCallback(async () => {
    try {
      const { data } = await getAgingReport();
      setAging(data);
    } catch {}
  }, []);

  useEffect(() => { if (tab === 'aging') fetchAging(); }, [tab, fetchAging]);

  const handleGenerate = async (type, quarter) => {
    setGenerating(`${type}-${quarter}`);
    try {
      const { data } = await generateTaxForm({ type, period_year: YEAR, period_quarter: quarter });
      toast.success(`Modelo ${type} generado`);
      setSelectedForm(data);
      setShowDetail(true);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setGenerating(null); }
  };

  const handleSubmit = async (formId) => {
    if (!window.confirm('¿Marcar como presentado? Esta acción no se puede deshacer.')) return;
    try {
      await submitTaxForm(formId);
      toast.success('Marcado como presentado');
      fetchData();
      setShowDetail(false);
    } catch { toast.error('Error'); }
  };

  const handleSaveConfig = async () => {
    try {
      await updateTaxConfig(config);
      toast.success('Configuración guardada');
      setLoading(true);
      fetchData();
    } catch { toast.error('Error'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div></div>;

  return (
    <PageTransition>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight text-stone-950" data-testid="tax-title">Impuestos</h1>
            <p className="text-stone-400 text-sm mt-1">Modelos fiscales, libros registro y calendario</p>
          </div>
          <PageHelpButton moduleKey="tax" variant="icon" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-stone-100 p-1 rounded-full w-fit flex-wrap">
          {[
            { key: 'models', icon: Calculator, label: 'Modelos' },
            { key: 'books', icon: BookOpen, label: 'Libros' },
            { key: 'aging', icon: AlertTriangle, label: 'Por Cobrar' },
            { key: 'calendar', icon: Calendar, label: 'Calendario' },
            { key: 'config', icon: Settings, label: 'Config' },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${tab === t.key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`} data-testid={`tax-tab-${t.key}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* MODELS TAB */}
        {tab === 'models' && (
          <div className="space-y-4" data-testid="models-tab">
            <h2 className="text-lg font-heading font-bold text-stone-900">Modelos Trimestrales {YEAR}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map(q => {
                const quarterModels = ['303', '130', '111', '115', '349'].filter(t => calendar.some(c => c.type === t && c.quarter === q));
                return (
                <div key={q} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
                  <h3 className="font-bold text-stone-900 mb-3">{q}T {YEAR}</h3>
                  <div className="space-y-2">
                    {(quarterModels.length > 0 ? quarterModels : ['303', '130']).map(type => {
                      const existing = forms.find(f => f.type === type && f.period_quarter === q);
                      const labels = { '303': 'IVA trimestral', '130': 'IRPF autónomos', '111': 'Retenciones IRPF', '115': 'Retenciones alquileres', '349': 'Intracomunitarias' };
                      return (
                        <div key={type} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-stone-400" />
                            <div>
                              <p className="text-sm font-medium text-stone-800">Modelo {type}</p>
                              <p className="text-[10px] text-stone-400">{labels[type] || type}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {existing && (
                              <>
                                <span className="text-xs font-bold tabular-nums text-stone-700">{fmt(existing.calculated_data?.resultado)}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${existing.status === 'submitted' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                                  {existing.status === 'submitted' ? 'Presentado' : 'Borrador'}
                                </span>
                              </>
                            )}
                            {existing && existing.status !== 'submitted' && (
                              <button onClick={() => { setSelectedForm(existing); setShowDetail(true); }} className="text-xs text-[#FF6B4A] font-medium hover:underline">Ver</button>
                            )}
                            <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => handleGenerate(type, q)} disabled={!!generating} data-testid={`generate-${type}-${q}`}>
                              {generating === `${type}-${q}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>

            {/* Annual models */}
            <h2 className="text-lg font-heading font-bold text-stone-900 mt-6">Modelos Anuales {YEAR}</h2>
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
              <div className="space-y-2">
                {['390', '190', '347'].filter(t => calendar.some(c => c.type === t)).map(type => {
                  const existing = forms.find(f => f.type === type && !f.period_quarter);
                  const labels = { '390': 'Resumen anual IVA', '190': 'Resumen anual retenciones', '347': 'Operaciones con terceros >3.005,06€' };
                  return (
                    <div key={type} className="flex items-center justify-between py-2 border-b border-stone-50 last:border-0">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-stone-400" />
                        <div>
                          <p className="text-sm font-medium text-stone-800">Modelo {type}</p>
                          <p className="text-[10px] text-stone-400">{labels[type]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {existing && (
                          <>
                            <span className="text-xs font-bold tabular-nums text-stone-700">{fmt(existing.calculated_data?.resultado)}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${existing.status === 'submitted' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                              {existing.status === 'submitted' ? 'Presentado' : 'Borrador'}
                            </span>
                          </>
                        )}
                        {existing && existing.status !== 'submitted' && (
                          <button onClick={() => { setSelectedForm(existing); setShowDetail(true); }} className="text-xs text-[#FF6B4A] font-medium hover:underline">Ver</button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => handleGenerate(type, null)} disabled={!!generating}>
                          {generating === `${type}-null` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* BOOKS TAB */}
        {tab === 'books' && (
          <div className="space-y-4" data-testid="books-tab">
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={libroType} onValueChange={setLibroType}>
                <SelectTrigger className="w-48 rounded-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="emitidas">Facturas Emitidas</SelectItem><SelectItem value="recibidas">Facturas Recibidas</SelectItem></SelectContent>
              </Select>
              <Select value={String(libroYear)} onValueChange={v => setLibroYear(parseInt(v))}>
                <SelectTrigger className="w-28 rounded-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{[YEAR, YEAR - 1, YEAR - 2].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={libroQ ? String(libroQ) : 'all'} onValueChange={v => setLibroQ(v === 'all' ? null : parseInt(v))}>
                <SelectTrigger className="w-28 rounded-full bg-white"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Anual</SelectItem><SelectItem value="1">1T</SelectItem><SelectItem value="2">2T</SelectItem><SelectItem value="3">3T</SelectItem><SelectItem value="4">4T</SelectItem></SelectContent>
              </Select>
            </div>

            {libro && (
              <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-auto">
                <div className="p-4 border-b border-stone-100 flex justify-between items-center">
                  <h3 className="font-bold text-stone-900">Libro de Facturas {libroType === 'emitidas' ? 'Emitidas' : 'Recibidas'}</h3>
                  <div className="flex gap-4 text-xs">
                    <span className="text-stone-500">Base: <strong className="text-stone-800">{fmt(libro.total_base)}</strong></span>
                    <span className="text-stone-500">IVA: <strong className="text-stone-800">{fmt(libro.total_iva)}</strong></span>
                    <span className="text-stone-500">Total: <strong className="text-stone-900">{fmt(libro.total_total)}</strong></span>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left py-2 px-4 font-semibold text-stone-400">{libroType === 'emitidas' ? 'Nº Factura' : 'Fecha'}</th>
                    <th className="text-left py-2 px-4 font-semibold text-stone-400">{libroType === 'emitidas' ? 'Fecha' : 'Proveedor'}</th>
                    <th className="text-left py-2 px-4 font-semibold text-stone-400">{libroType === 'emitidas' ? 'Cliente' : 'NIF'}</th>
                    <th className="text-left py-2 px-4 font-semibold text-stone-400">NIF</th>
                    <th className="text-right py-2 px-4 font-semibold text-stone-400">Base</th>
                    <th className="text-right py-2 px-4 font-semibold text-stone-400">IVA</th>
                    <th className="text-right py-2 px-4 font-semibold text-stone-400">Total</th>
                  </tr></thead>
                  <tbody>
                    {libro.entries.map((e, i) => (
                      <tr key={i} className="border-b border-stone-50 hover:bg-orange-50/30">
                        <td className="py-2 px-4 text-stone-800 font-medium">{libroType === 'emitidas' ? e.invoice_number : e.date}</td>
                        <td className="py-2 px-4 text-stone-600">{libroType === 'emitidas' ? e.issue_date : e.vendor}</td>
                        <td className="py-2 px-4 text-stone-600">{libroType === 'emitidas' ? e.client_name : e.vendor_tax_id}</td>
                        <td className="py-2 px-4 text-stone-400">{libroType === 'emitidas' ? e.client_tax_id : e.category}</td>
                        <td className="py-2 px-4 text-right tabular-nums text-stone-700">{fmt(e.base_imponible)}</td>
                        <td className="py-2 px-4 text-right tabular-nums text-stone-500">{fmt(e.cuota_iva)}</td>
                        <td className="py-2 px-4 text-right tabular-nums font-bold text-stone-900">{fmt(e.total)}</td>
                      </tr>
                    ))}
                    {libro.entries.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-stone-400">Sin registros para este período</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* AGING TAB */}
        {tab === 'aging' && aging && (
          <div className="space-y-4" data-testid="aging-tab">
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-5">
              <h3 className="font-heading font-bold text-stone-900 mb-1">Total Pendiente de Cobro</h3>
              <p className="text-3xl font-extrabold text-[#FF6B4A]">{fmt(aging.total_pending)}</p>
            </div>
            <StaggerContainer className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { key: 'current', label: 'Al día', color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
                { key: '30', label: '1-30 días', color: 'bg-amber-50 text-amber-700 border-amber-100' },
                { key: '60', label: '31-60 días', color: 'bg-orange-50 text-orange-700 border-orange-100' },
                { key: '90', label: '61-90 días', color: 'bg-red-50 text-red-700 border-red-100' },
                { key: 'over90', label: '+90 días', color: 'bg-red-100 text-red-800 border-red-200' },
              ].map(b => (
                <StaggerItem key={b.key}>
                  <div className={`rounded-2xl border p-4 ${b.color}`} data-testid={`aging-${b.key}`}>
                    <p className="text-xs font-semibold opacity-70">{b.label}</p>
                    <p className="text-xl font-extrabold mt-1">{fmt(aging.buckets[b.key]?.total)}</p>
                    <p className="text-xs opacity-60 mt-0.5">{aging.buckets[b.key]?.count || 0} facturas</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            {/* Invoice list per bucket */}
            {['30', '60', '90', 'over90'].map(bk => (
              aging.buckets[bk]?.invoices?.length > 0 && (
                <div key={bk} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 overflow-hidden">
                  <div className="px-5 py-3 border-b border-stone-100 bg-red-50/30">
                    <h4 className="text-sm font-bold text-stone-800">{bk === '30' ? '1-30 días' : bk === '60' ? '31-60 días' : bk === '90' ? '61-90 días' : '+90 días'} vencido</h4>
                  </div>
                  <div className="divide-y divide-stone-50">
                    {aging.buckets[bk].invoices.map(inv => (
                      <div key={inv.id} className="flex items-center justify-between px-5 py-2.5">
                        <div>
                          <p className="text-sm font-medium text-stone-800">{inv.invoice_number} - {inv.client_name}</p>
                          <p className="text-xs text-stone-400">Vence: {inv.due_date} ({inv.days_overdue} días)</p>
                        </div>
                        <span className="font-bold text-sm text-red-500 tabular-nums">{fmt(inv.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* CALENDAR TAB */}
        {tab === 'calendar' && (
          <div className="space-y-3" data-testid="calendar-tab">
            {calendar.map((d, i) => {
              const isPast = d.deadline < new Date().toISOString().split('T')[0];
              const isUrgent = !isPast && d.deadline <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
              return (
                <div key={i} className={`flex items-center gap-4 bg-white rounded-2xl border p-4 transition-all ${d.status === 'submitted' ? 'border-emerald-100 opacity-60' : isUrgent ? 'border-red-200 bg-red-50/30' : 'border-stone-100'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${d.status === 'submitted' ? 'bg-emerald-50' : isUrgent ? 'bg-red-50' : 'bg-stone-50'}`}>
                    {d.status === 'submitted' ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : isUrgent ? <AlertTriangle className="w-5 h-5 text-red-500" /> : <Clock className="w-5 h-5 text-stone-400" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-stone-800">{d.label}</p>
                    <p className="text-xs text-stone-400">Vence: {d.deadline}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${d.status === 'submitted' ? 'bg-emerald-50 text-emerald-600' : d.status === 'draft' ? 'bg-amber-50 text-amber-600' : 'bg-stone-100 text-stone-500'}`}>
                      {d.status === 'submitted' ? 'Presentado' : d.status === 'draft' ? 'Borrador' : 'Pendiente'}
                    </span>
                    {d.status !== 'submitted' && d.type && ['303', '130'].includes(d.type) && d.quarter && (
                      <Button size="sm" variant="outline" className="rounded-full text-xs h-7" onClick={() => handleGenerate(d.type, d.quarter)}>
                        <Calculator className="w-3 h-3 mr-1" />Calcular
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CONFIG TAB */}
        {tab === 'config' && config && (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6 max-w-xl" data-testid="config-tab">
            <h3 className="font-heading font-bold text-stone-900 mb-4">Configuración Fiscal</h3>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-xs">Tipo de empresa</Label>
                <Select value={config.company_type || 'autonomo'} onValueChange={v => setConfig({ ...config, company_type: v })}>
                  <SelectTrigger className="rounded-xl bg-stone-50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="autonomo">Autónomo (Persona Física)</SelectItem>
                    <SelectItem value="sl">Sociedad Limitada (S.L.)</SelectItem>
                    <SelectItem value="slp">Sociedad Limitada Profesional (S.L.P.)</SelectItem>
                    <SelectItem value="sa">Sociedad Anónima (S.A.)</SelectItem>
                    <SelectItem value="cb">Comunidad de Bienes (C.B.)</SelectItem>
                    <SelectItem value="cooperativa">Cooperativa</SelectItem>
                    <SelectItem value="asociacion">Asociación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Régimen fiscal</Label>
                <Select value={config.tax_regime || 'estimacion_directa_simplificada'} onValueChange={v => setConfig({ ...config, tax_regime: v })}>
                  <SelectTrigger className="rounded-xl bg-stone-50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estimacion_directa_simplificada">Estimación Directa Simplificada</SelectItem>
                    <SelectItem value="estimacion_directa_normal">Estimación Directa Normal</SelectItem>
                    <SelectItem value="modulos">Módulos (Estimación Objetiva)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Región</Label>
                <Select value={config.region || 'general'} onValueChange={v => setConfig({ ...config, region: v })}>
                  <SelectTrigger className="rounded-xl bg-stone-50 border-transparent"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General (Territorio Común)</SelectItem>
                    <SelectItem value="pais_vasco_alava">País Vasco - Álava</SelectItem>
                    <SelectItem value="pais_vasco_bizkaia">País Vasco - Bizkaia</SelectItem>
                    <SelectItem value="pais_vasco_gipuzkoa">País Vasco - Gipuzkoa</SelectItem>
                    <SelectItem value="navarra">Navarra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Retención IRPF (%)</Label>
                <Input type="number" step="0.5" value={config.irpf_retention_rate || 15} onChange={e => setConfig({ ...config, irpf_retention_rate: parseFloat(e.target.value) })} className="rounded-xl bg-stone-50 border-transparent" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Epígrafe IAE</Label>
                <Input value={config.iae_section || ''} onChange={e => setConfig({ ...config, iae_section: e.target.value })} placeholder="Ej: 841" className="rounded-xl bg-stone-50 border-transparent" />
              </div>
              <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Modelos aplicables</p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={config.has_employees || false} onChange={e => setConfig({ ...config, has_employees: e.target.checked })} className="w-4 h-4 rounded border-stone-300 text-[#FF6B4A] focus:ring-[#FF6B4A]" />
                  <div><p className="text-sm text-stone-800 font-medium">Tengo empleados/profesionales</p><p className="text-[10px] text-stone-400">Activa Modelo 111 (retenciones IRPF)</p></div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={config.has_rentals || false} onChange={e => setConfig({ ...config, has_rentals: e.target.checked })} className="w-4 h-4 rounded border-stone-300 text-[#FF6B4A] focus:ring-[#FF6B4A]" />
                  <div><p className="text-sm text-stone-800 font-medium">Pago alquiler de local/oficina</p><p className="text-[10px] text-stone-400">Activa Modelo 115 (retenciones alquileres)</p></div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={config.intracom_operations || false} onChange={e => setConfig({ ...config, intracom_operations: e.target.checked })} className="w-4 h-4 rounded border-stone-300 text-[#FF6B4A] focus:ring-[#FF6B4A]" />
                  <div><p className="text-sm text-stone-800 font-medium">Operaciones intracomunitarias</p><p className="text-[10px] text-stone-400">Activa Modelo 349 (declaración intracomunitaria)</p></div>
                </label>
              </div>
              <Button onClick={handleSaveConfig} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full w-full" data-testid="save-tax-config">Guardar Configuración</Button>
            </div>
          </div>
        )}

        {/* FORM DETAIL MODAL */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedForm && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading font-extrabold">
                    Modelo {selectedForm.type} - {selectedForm.period_quarter}T {selectedForm.period_year}
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${selectedForm.status === 'submitted' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                      {selectedForm.status === 'submitted' ? 'Presentado' : 'Borrador'}
                    </span>
                    <span className="text-xs text-stone-400">Generado: {selectedForm.generated_at?.split('T')[0]}</span>
                  </div>

                  {selectedForm.type === '303' && selectedForm.calculated_data && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold text-stone-800 border-b border-stone-100 pb-1">IVA Devengado (Repercutido)</h4>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Base 21%</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_01)}</p></div>
                        <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Base 10%</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_04)}</p></div>
                        <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Base 4%</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_07)}</p></div>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-3"><p className="text-xs text-emerald-700">Total IVA Devengado (C.27)</p><p className="text-lg font-bold text-emerald-800">{fmt(selectedForm.calculated_data.casilla_27)}</p><p className="text-[10px] text-emerald-600">{selectedForm.calculated_data.num_invoices} facturas</p></div>

                      <h4 className="text-sm font-bold text-stone-800 border-b border-stone-100 pb-1">IVA Deducible (Soportado)</h4>
                      <div className="bg-sky-50 rounded-xl p-3"><p className="text-xs text-sky-700">Total Deducciones (C.40)</p><p className="text-lg font-bold text-sky-800">{fmt(selectedForm.calculated_data.casilla_40)}</p><p className="text-[10px] text-sky-600">{selectedForm.calculated_data.num_expenses} gastos</p></div>

                      <h4 className="text-sm font-bold text-stone-800 border-b border-stone-100 pb-1">Resultado</h4>
                      <div className={`rounded-xl p-4 ${selectedForm.calculated_data.resultado >= 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                        <p className="text-xs text-stone-600">Resultado (C.69) — {selectedForm.calculated_data.resultado >= 0 ? 'A ingresar' : 'A compensar'}</p>
                        <p className={`text-2xl font-extrabold ${selectedForm.calculated_data.resultado >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(selectedForm.calculated_data.resultado)}</p>
                      </div>

                      {/* VAT group detail */}
                      {selectedForm.calculated_data.vat_groups && Object.entries(selectedForm.calculated_data.vat_groups).map(([rate, data]) => (
                        <details key={rate} className="text-xs">
                          <summary className="cursor-pointer text-stone-500 hover:text-stone-700 font-medium">Ver facturas IVA {rate}% ({data.invoice_numbers?.length || 0} facturas)</summary>
                          <p className="mt-1 text-stone-400 pl-4">{data.invoice_numbers?.join(', ') || 'N/A'}</p>
                        </details>
                      ))}
                    </div>
                  )}

                  {selectedForm.type === '130' && selectedForm.calculated_data && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-emerald-50 rounded-xl p-3"><p className="text-xs text-emerald-700">Ingresos (C.01)</p><p className="text-lg font-bold text-emerald-800">{fmt(selectedForm.calculated_data.casilla_01)}</p></div>
                        <div className="bg-red-50 rounded-xl p-3"><p className="text-xs text-red-700">Gastos (C.02)</p><p className="text-lg font-bold text-red-800">{fmt(selectedForm.calculated_data.casilla_02)}</p></div>
                      </div>
                      <div className="bg-stone-50 rounded-xl p-3"><p className="text-xs text-stone-600">Rendimiento Neto (C.03)</p><p className="text-lg font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_03)}</p></div>
                      <div className="bg-stone-50 rounded-xl p-3"><p className="text-xs text-stone-600">20% del rendimiento (C.05)</p><p className="text-lg font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_05)}</p></div>
                      <div className="bg-stone-50 rounded-xl p-3"><p className="text-xs text-stone-600">Pagos anteriores (C.06)</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_06)}</p></div>
                      <div className={`rounded-xl p-4 ${selectedForm.calculated_data.resultado > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                        <p className="text-xs text-stone-600">A ingresar (C.07)</p>
                        <p className={`text-2xl font-extrabold ${selectedForm.calculated_data.resultado > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(selectedForm.calculated_data.resultado)}</p>
                      </div>
                    </div>
                  )}

                  {/* Generic detail for models 111, 115, 349, 347, 390, 190 */}
                  {!['303', '130'].includes(selectedForm.type) && selectedForm.calculated_data && (
                    <div className="space-y-3">
                      {selectedForm.calculated_data.description && (
                        <p className="text-sm text-stone-500 italic">{selectedForm.calculated_data.description}</p>
                      )}
                      <div className={`rounded-xl p-4 ${(selectedForm.calculated_data.resultado || 0) > 0 ? 'bg-amber-50' : 'bg-stone-50'}`}>
                        <p className="text-xs text-stone-600">Resultado</p>
                        <p className="text-2xl font-extrabold text-stone-900">{fmt(selectedForm.calculated_data.resultado)}</p>
                      </div>

                      {/* Model-specific fields */}
                      {selectedForm.type === '111' && (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Perceptores</p><p className="font-bold text-stone-800">{selectedForm.calculated_data.casilla_01}</p></div>
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Base</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_02)}</p></div>
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Retenciones</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_03)}</p></div>
                        </div>
                      )}

                      {selectedForm.type === '115' && (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Arrendadores</p><p className="font-bold text-stone-800">{selectedForm.calculated_data.casilla_01}</p></div>
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Base</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_02)}</p></div>
                          <div className="bg-stone-50 rounded-lg p-3"><p className="text-stone-400">Retenciones</p><p className="font-bold text-stone-800">{fmt(selectedForm.calculated_data.casilla_03)}</p></div>
                        </div>
                      )}

                      {selectedForm.type === '349' && selectedForm.calculated_data.by_client && (
                        <div>
                          <p className="text-xs font-semibold text-stone-600 mb-2">Operadores ({selectedForm.calculated_data.num_operadores})</p>
                          {Object.entries(selectedForm.calculated_data.by_client).map(([name, data]) => (
                            <div key={name} className="flex justify-between py-1.5 border-b border-stone-50 text-xs">
                              <span className="text-stone-700">{name}</span>
                              <span className="font-bold text-stone-800">{fmt(data.base)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedForm.type === '347' && (
                        <div className="space-y-3">
                          <p className="text-xs text-stone-500">Operaciones superiores a 3.005,06€/año</p>
                          {selectedForm.calculated_data.ventas_declarables?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-emerald-600 mb-1">Ventas ({selectedForm.calculated_data.ventas_declarables.length})</p>
                              {selectedForm.calculated_data.ventas_declarables.map((v, i) => (
                                <div key={i} className="flex justify-between py-1 text-xs"><span className="text-stone-700">{v.name}</span><span className="font-bold">{fmt(v.total)}</span></div>
                              ))}
                            </div>
                          )}
                          {selectedForm.calculated_data.compras_declarables?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-red-600 mb-1">Compras ({selectedForm.calculated_data.compras_declarables.length})</p>
                              {selectedForm.calculated_data.compras_declarables.map((v, i) => (
                                <div key={i} className="flex justify-between py-1 text-xs"><span className="text-stone-700">{v.name}</span><span className="font-bold">{fmt(v.total)}</span></div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedForm.type === '390' && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-emerald-50 rounded-lg p-3"><p className="text-emerald-700">IVA Repercutido</p><p className="font-bold text-emerald-800">{fmt(selectedForm.calculated_data.total_iva_repercutido)}</p></div>
                            <div className="bg-sky-50 rounded-lg p-3"><p className="text-sky-700">IVA Soportado</p><p className="font-bold text-sky-800">{fmt(selectedForm.calculated_data.total_iva_soportado)}</p></div>
                          </div>
                          <div className="bg-stone-50 rounded-lg p-3 text-xs"><p className="text-stone-500">Ingresado trimestral</p><p className="font-bold">{fmt(selectedForm.calculated_data.total_ingresado_trimestral)}</p></div>
                          {selectedForm.calculated_data.quarterly_data?.map(q => (
                            <div key={q.quarter} className="flex justify-between text-xs py-1 border-b border-stone-50">
                              <span className="text-stone-500">{q.quarter}T</span><span className="font-bold">{fmt(q.resultado_303)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedForm.type === '190' && selectedForm.calculated_data.perceptores?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-stone-600 mb-2">Perceptores ({selectedForm.calculated_data.num_perceptores})</p>
                          {selectedForm.calculated_data.perceptores.map((p, i) => (
                            <div key={i} className="flex justify-between py-1.5 border-b border-stone-50 text-xs">
                              <div><span className="text-stone-700 font-medium">{p.name}</span> <span className="text-stone-400">{p.tax_id}</span></div>
                              <div className="text-right"><span className="text-stone-600">Base: {fmt(p.base)}</span> <span className="font-bold text-stone-800 ml-2">Ret: {fmt(p.retention)}</span></div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedForm.status !== 'submitted' && (
                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" className="flex-1 rounded-full" onClick={() => setShowDetail(false)}>Cerrar</Button>
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full" onClick={() => handleSubmit(selectedForm.id)}>
                        <Send className="w-4 h-4 mr-1.5" />Marcar Presentado
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </PageTransition>
  );
};

export default TaxPage;
